import { z } from 'zod';
import { MaintenanceCategory } from '../maintenance.js';
import { Id, Money, Quantity, ShortText, VatRate, amount } from './common.js';

/**
 * Catalogue de prestations du garage (08/10/2026, council n°2, priorité 2). Une prestation porte un temps
 * de main-d'œuvre (heures) et des pièces types (prix HT) ; la ligne de main-d'œuvre est préremplie avec
 * le taux horaire HT du garage (temps × taux). Lecture : `complement.draft` ; écriture : `settings.manage`.
 * Montants en chaînes décimales exactes, retirés des réponses sans `amounts.view` (`amount()`).
 */
export const CATALOG_PARTS_MAX = 20;

export const CatalogPartInput = z.strictObject({
  label: ShortText,
  reference: ShortText.nullable().optional(),
  quantity: Quantity,
  unitPriceHt: Money,
});
export type CatalogPartInput = z.output<typeof CatalogPartInput>;

export const CatalogPartOutput = z.object({
  label: z.string(),
  reference: z.string().nullable(),
  quantity: Quantity,
  unitPriceHt: amount(),
});

export const CatalogItemInput = z.strictObject({
  label: ShortText,
  maintenanceCategory: MaintenanceCategory.nullable().optional(),
  /** Temps de main-d'œuvre en heures (« 1.5 ») ; 0 = pas de ligne de main-d'œuvre. */
  laborHours: Quantity,
  /** Absent : TVA par défaut du garage. */
  vatRate: VatRate.optional(),
  parts: z.array(CatalogPartInput).max(CATALOG_PARTS_MAX).default([]),
  sortOrder: z.int().min(0).optional(),
});

export const CatalogItemUpdateInput = z.strictObject({
  label: ShortText.optional(),
  maintenanceCategory: MaintenanceCategory.nullable().optional(),
  laborHours: Quantity.optional(),
  vatRate: VatRate.optional(),
  parts: z.array(CatalogPartInput).max(CATALOG_PARTS_MAX).optional(),
  sortOrder: z.int().min(0).optional(),
  /** Retirer du catalogue (les travaux déjà créés ne changent pas). */
  archived: z.boolean().optional(),
});

export const CatalogItemOutput = z.object({
  id: Id,
  label: z.string(),
  maintenanceCategory: MaintenanceCategory.nullable(),
  laborHours: Quantity,
  vatRate: VatRate,
  parts: z.array(CatalogPartOutput),
  sortOrder: z.int(),
  archived: z.boolean(),
  /** Main-d'œuvre HT (temps × taux), `null` sans taux horaire. */
  laborHt: amount(),
  /** Total estimé (main-d'œuvre + pièces), `null` sans taux horaire alors qu'il y a du temps. */
  totalHt: amount(),
  totalTtc: amount(),
});
export type CatalogItemOutput = z.infer<typeof CatalogItemOutput>;

export const CatalogOutput = z.object({
  /** Taux horaire HT du garage (`garage.updateSettings`), `null` s'il n'est pas réglé. */
  hourlyRateHt: amount(),
  items: z.array(CatalogItemOutput),
});
export type CatalogOutput = z.infer<typeof CatalogOutput>;

export const CatalogListQuery = z.strictObject({
  /** `1` : inclure les prestations retirées (écran de réglage). */
  archived: z.enum(['0', '1']).optional(),
});
